import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser, STATUS_LABELS, STATUS_COLORS, PRIORITY_COLORS, workflowFor } from '../UserContext';
import InstallFlow from './InstallFlow';
import FMPPreviewModal from './FMPPreviewModal';
import JHAForm from './JHAForm';
import PhotoUpload from './PhotoUpload';
import MaterialPanel from './MaterialPanel';
import { MopDocumentUpload, MopDocumentViewer } from './MopDocuments';
import { useWorkItemSync } from '../hooks/useWorkItemSync';

const API = `${window.location.protocol}//${window.location.hostname}/api`;
function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(r => r.json());
}

function CheckItem({ step, onToggle, onDelete, onEdit, canEdit }) {
  const [editing, setEditing] = useState(false);
  const [desc, setDesc] = useState(step.description);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 12px', backgroundColor: step.completed ? '#0d1f0d' : '#1e293b', borderRadius: 7, marginBottom: 5, border: `1px solid ${step.completed ? '#16632a44' : '#334155'}` }}>
      <div onClick={() => onToggle(step, !step.completed)} style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${step.completed ? '#22c55e' : '#475569'}`, backgroundColor: step.completed ? '#22c55e' : 'transparent', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
        {step.completed && <span style={{ color: 'white', fontSize: 12, fontWeight: 'bold' }}>✓</span>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <input autoFocus value={desc} onChange={e => setDesc(e.target.value)}
            onBlur={() => { onEdit(step, desc); setEditing(false); }}
            onKeyDown={e => { if (e.key === 'Enter') { onEdit(step, desc); setEditing(false); } if (e.key === 'Escape') { setDesc(step.description); setEditing(false); } }}
            style={{ width: '100%', backgroundColor: '#0f172a', border: '1px solid #3b82f6', borderRadius: 4, color: 'white', fontSize: 13, padding: '3px 6px', boxSizing: 'border-box' }}
          />
        ) : (
          <div style={{ color: step.completed ? '#4ade80' : '#e2e8f0', fontSize: 13, textDecoration: step.completed ? 'line-through' : 'none', opacity: step.completed ? 0.7 : 1 }}
            onDoubleClick={() => canEdit && setEditing(true)}>
            {step.description}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
          <span style={{ fontSize: 10, color: '#475569', backgroundColor: '#0f172a', padding: '1px 6px', borderRadius: 3 }}>{step.category}</span>
          {step.completed_by_name && <span style={{ fontSize: 10, color: '#64748b' }}>✓ {step.completed_by_name}</span>}
        </div>
      </div>
      {canEdit && <button onClick={() => onDelete(step)} style={{ backgroundColor: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 13, padding: '2px 4px' }}>✕</button>}
    </div>
  );
}

const GATE_METERS = 152; // 500 feet

function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function SiteVisitForm({ workItemId, siteLat, siteLon, onSubmit }) {
  const user = useUser();
  const isAdmin = user?.role === 'admin';
  const [form, setForm] = useState({
    power_available: null, rack_space_ok: null, fiber_accessible: null, site_safe: null,
    scope_confirmed: false, site_conditions: '', markups: '',
    gps_verified: false, gps_latitude: null, gps_longitude: null, gps_accuracy_m: null,
    distance_to_site_m: null,
  });
  const [gpsStatus, setGpsStatus] = useState('idle'); // idle | getting | ok | too_far | error | no_site
  const [distanceM, setDistanceM] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const getGps = () => {
    if (!siteLat || !siteLon) { setGpsStatus('no_site'); return; }
    if (!navigator.geolocation) { setGpsStatus('error'); return; }
    setGpsStatus('getting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const dist = Math.round(haversineMeters(pos.coords.latitude, pos.coords.longitude, siteLat, siteLon));
        const verified = dist <= GATE_METERS;
        setDistanceM(dist);
        setForm(p => ({
          ...p,
          gps_verified: verified,
          gps_latitude: pos.coords.latitude,
          gps_longitude: pos.coords.longitude,
          gps_accuracy_m: Math.round(pos.coords.accuracy),
          distance_to_site_m: dist,
        }));
        setGpsStatus(verified ? 'ok' : 'too_far');
      },
      () => setGpsStatus('error'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = async () => {
    if (!form.gps_verified && !isAdmin) { setError('GPS verification required — tap Verify GPS first.'); return; }
    setError(null);
    setSubmitting(true);
    await onSubmit(form);
    setSubmitting(false);
  };

  const BoolField = ({ label, field }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', gap: 6 }}>
        {[['Yes', true], ['No', false], ['N/A', null]].map(([l, v]) => (
          <button key={l} onClick={() => setForm(p => ({ ...p, [field]: v }))} style={{
            flex: 1, padding: '5px 4px', borderRadius: 6, border: `1px solid ${form[field] === v ? 'transparent' : '#334155'}`, cursor: 'pointer', fontSize: 12, fontWeight: form[field] === v ? 'bold' : 'normal',
            backgroundColor: form[field] === v ? (v === true ? '#166534' : v === false ? '#7f1d1d' : '#374151') : '#0f172a',
            color: form[field] === v ? 'white' : '#64748b',
          }}>{l}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: 16, border: '1px solid #334155' }}>
      <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 14, marginBottom: 14 }}>Site Visit Checklist</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
        <BoolField label="Power Available" field="power_available" />
        <BoolField label="Rack Space OK" field="rack_space_ok" />
        <BoolField label="Fiber Accessible" field="fiber_accessible" />
        <BoolField label="Site Safe" field="site_safe" />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>Site Conditions / Observations</label>
        <textarea value={form.site_conditions} onChange={e => setForm(p => ({ ...p, site_conditions: e.target.value }))} rows={2}
          style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>Scope Adjustments / Markups</label>
        <textarea value={form.markups} onChange={e => setForm(p => ({ ...p, markups: e.target.value }))} rows={2}
          style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <input type="checkbox" id="scope" checked={form.scope_confirmed} onChange={e => setForm(p => ({ ...p, scope_confirmed: e.target.checked }))} />
        <label htmlFor="scope" style={{ color: '#e2e8f0', fontSize: 13 }}>Scope confirmed — site ready to proceed</label>
      </div>

      {/* GPS verification */}
      <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 7, backgroundColor: '#0f172a', border: `1px solid ${form.gps_verified ? '#166534' : gpsStatus === 'too_far' ? '#7f1d1d' : '#334155'}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 'bold', color: form.gps_verified ? '#4ade80' : gpsStatus === 'too_far' ? '#f87171' : '#94a3b8' }}>
              {form.gps_verified && '✓ GPS Verified — within 500 ft of site'}
              {gpsStatus === 'too_far' && `✗ Too far from site — ${distanceM}m away (max 152m)`}
              {gpsStatus === 'idle' && '📍 GPS Verification Required'}
              {gpsStatus === 'getting' && '📍 Getting location…'}
              {gpsStatus === 'error' && '✗ Could not get location — check permissions'}
              {gpsStatus === 'no_site' && '✗ No GPS coordinates on this location — set them in the map first'}
            </div>
            {form.gps_verified && (
              <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>
                {form.gps_latitude?.toFixed(5)}, {form.gps_longitude?.toFixed(5)} · ±{form.gps_accuracy_m}m · {distanceM}m from site
              </div>
            )}
            {gpsStatus === 'too_far' && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>You must be on-site to submit a site visit.</div>
            )}
          </div>
          {!form.gps_verified && gpsStatus !== 'getting' && (
            <button onClick={getGps} style={{ padding: '6px 12px', backgroundColor: '#1e3a5f', color: '#93c5fd', border: '1px solid #1d4ed8', borderRadius: 6, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {gpsStatus === 'too_far' ? 'Retry GPS' : 'Verify GPS'}
            </button>
          )}
          {!form.gps_verified && isAdmin && (
            <button onClick={() => { setForm(p => ({ ...p, gps_verified: true, distance_to_site_m: 0 })); setDistanceM(0); setGpsStatus('ok'); }}
              style={{ padding: '6px 12px', backgroundColor: '#4c1d95', color: '#ddd6fe', border: '1px solid #6d28d9', borderRadius: 6, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>
              Admin Override
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 10 }}>{error}</div>}

      <button onClick={handleSubmit} disabled={submitting} style={{ width: '100%', padding: 10, backgroundColor: submitting ? '#374151' : '#2563eb', color: 'white', border: 'none', borderRadius: 7, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 'bold' }}>
        {submitting ? 'Submitting…' : 'Submit Site Visit'}
      </button>
    </div>
  );
}

function FMPForm({ workItemId, racks, locationId, navigate, onSubmit }) {
  const [form, setForm] = useState({ rack_id: '', fmp_type: 'rack_view', no_change: false, change_summary: '', notes: '' });
  const [adding, setAdding] = useState(false);
  const addRack = async () => {
    if (!locationId) { alert('This job has no site/location set — set one on the work order first.'); return; }
    const label = window.prompt('Name this rack (e.g. "Rack A1"):');
    if (!label) return;
    setAdding(true);
    try {
      const r = await api('/racks', { method: 'POST', body: JSON.stringify({ location_id: locationId, label }) });
      navigate(`/rack/${r.id}`);
    } catch (e) { alert(e.message); } finally { setAdding(false); }
  };
  return (
    <div style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: 16, border: '1px solid #334155' }}>
      <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 14, marginBottom: 14 }}>Generate FMP</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>FMP Type</label>
          <select value={form.fmp_type} onChange={e => setForm(p => ({ ...p, fmp_type: e.target.value }))}
            style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13 }}>
            {[['rack_view','Rack View'],['cassette','Cassette Layout'],['routing','Routing Diagram'],['splicing','Splicing Diagram']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>Rack</label>
          <select value={form.rack_id} onChange={e => setForm(p => ({ ...p, rack_id: e.target.value }))}
            style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13 }}>
            <option value=''>{racks.length ? '— Select rack —' : '— No rack yet —'}</option>
            {racks.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          <button type="button" onClick={addRack} disabled={adding}
            style={{ marginTop: 6, width: '100%', padding: '6px', backgroundColor: '#0f1e33', color: '#93c5fd', border: '1px solid #1e3a5f', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
            {adding ? 'Creating…' : (racks.length ? '+ Build another rack' : '+ Build a rack at this site →')}
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input type="checkbox" id="no_change" checked={form.no_change} onChange={e => setForm(p => ({ ...p, no_change: e.target.checked }))} />
        <label htmlFor="no_change" style={{ color: '#e2e8f0', fontSize: 13 }}>No change — marking as reviewed, no updates needed</label>
      </div>
      {!form.no_change && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>Change Summary <span style={{ color: '#f87171' }}>*</span></label>
          <textarea value={form.change_summary} onChange={e => setForm(p => ({ ...p, change_summary: e.target.value }))} rows={2}
            placeholder="Describe what changed: equipment installed, ports patched, etc."
            style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: `1px solid ${form.change_summary ? '#334155' : '#7f1d1d'}`, borderRadius: 7, color: 'white', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
          {!form.change_summary && <div style={{ color: '#f87171', fontSize: 11, marginTop: 4 }}>Required — describe the work that was done</div>}
        </div>
      )}
      <button onClick={() => onSubmit({ ...form, rack_id: form.rack_id || null })}
        disabled={!form.no_change && !form.change_summary}
        style={{ width: '100%', padding: 10, backgroundColor: (!form.no_change && !form.change_summary) ? '#374151' : '#f97316', color: (!form.no_change && !form.change_summary) ? '#6b7280' : 'white', border: 'none', borderRadius: 7, cursor: (!form.no_change && !form.change_summary) ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 'bold' }}>
        {(!form.no_change && !form.change_summary) ? 'Fill in Change Summary to Generate' : 'Generate FMP'}
      </button>
    </div>
  );
}

export default function WorkItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useUser();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('mop');
  const [racks, setRacks] = useState([]);
  const [newStep, setNewStep] = useState('');
  const [showSiteVisit, setShowSiteVisit] = useState(false);
  const [showFMPForm, setShowFMPForm] = useState(false);
  const [fmpPreview, setFmpPreview] = useState(null);
  const [fmpPreviewForm, setFmpPreviewForm] = useState(null);
  const [submittingFmp, setSubmittingFmp] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [reassignTech, setReassignTech] = useState('');
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeRack, setCloseRack] = useState('');
  const [closeMatches, setCloseMatches] = useState(null);
  const [closeNotes, setCloseNotes] = useState('');
  const [closingBusy, setClosingBusy] = useState(false);
  const [showJHA, setShowJHA] = useState(false);
  const [jhaInfo, setJhaInfo] = useState(null); // { id, site_arrival_at, status }
  const [showArrivalFmp, setShowArrivalFmp] = useState(false);
  const [arrivalRack, setArrivalRack] = useState('');
  const [arrivalMatches, setArrivalMatches] = useState(null);
  const [arrivalNotes, setArrivalNotes] = useState('');
  const [arrivalBusy, setArrivalBusy] = useState(false);
  const [capitalBusy, setCapitalBusy] = useState(false);
  const [capitalDismissed, setCapitalDismissed] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnCheckout, setReturnCheckout] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [returnChecks, setReturnChecks] = useState({});
  const [returnBusy, setReturnBusy] = useState(false);
  const geoWatchRef = useRef(null);

  const load = useCallback(() => {
    api(`/work-items/${id}`).then(data => { setItem(data); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const itemIdNum = parseInt(id);
  useWorkItemSync(useCallback(msg => {
    if ((msg.type === 'work_item_updated' || msg.type === 'work_item_acceptance_updated') && msg.id === itemIdNum) {
      load();
    }
  }, [load, itemIdNum]));

  useEffect(() => {
    if (item?.location_id) {
      api(`/racks/location/${item.location_id}`).then(r => setRacks(Array.isArray(r) ? r : []));
    }
  }, [item?.location_id]);

  useEffect(() => {
    api('/users').then(r => setUsers(Array.isArray(r) ? r : []));
  }, []);

  useEffect(() => {
    api('/locations').then(r => setLocations(Array.isArray(r) ? r : [])).catch(() => {});
  }, []);

  // Incidents/WOTs have no MOP / Site Visit / FMP; if we land on one of those tabs, fall back to History.
  // Material stays available as a tab for incidents — it's just no longer the default.
  useEffect(() => {
    if (item?.work_category !== 'Project' && ['mop','site_visit','fmps'].includes(tab)) {
      setTab('history');
    }
  }, [item?.work_category, tab]);

  const setLocation = async (locId) => {
    if (!locId) return;
    const res = await api(`/work-items/${id}`, { method: 'PATCH', body: JSON.stringify({ location_id: Number(locId) }) });
    if (res?.error) { alert(res.error); return; }
    load();
  };

  const loadJha = useCallback(async () => {
    const res = await api(`/jhas?work_item_id=${id}`);
    if (Array.isArray(res) && res[0]) {
      const full = await api(`/jhas/${res[0].id}`);
      setJhaInfo({ id: full.id, site_arrival_at: full.site_arrival_at, status: full.status });
    }
  }, [id]);

  // Load JHA info when item is in_progress
  useEffect(() => {
    if (item?.status === 'in_progress' && ['tech', 'field_engineer', 'admin'].includes(user?.role) && !jhaInfo) {
      loadJha();
    }
  }, [item?.status, jhaInfo, loadJha, user?.role]);

  // Geofence watcher — auto-set site_arrival_at when within 500ft
  useEffect(() => {
    const isTech = ['tech', 'field_engineer'].includes(user?.role);
    if (!item || item.status !== 'in_progress' || !item.latitude || !item.longitude || !isTech) return;
    if (!jhaInfo) { loadJha(); return; }
    if (jhaInfo.site_arrival_at || !jhaInfo.id) return;
    if (!navigator.geolocation) return;

    let cleared = false;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        if (cleared) return;
        const dist = haversineMeters(pos.coords.latitude, pos.coords.longitude, item.latitude, item.longitude);
        if (dist <= GATE_METERS) {
          cleared = true;
          navigator.geolocation.clearWatch(watchId);
          const arrivalTime = new Date().toISOString();
          api(`/jhas/${jhaInfo.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ site_arrival_at: arrivalTime }),
          }).then(() => {
            setJhaInfo(prev => ({ ...prev, site_arrival_at: arrivalTime }));
            if (racks.length > 0) {
              setArrivalRack(String(racks[0].id));
              setArrivalMatches(null);
              setArrivalNotes('');
              setShowArrivalFmp(true);
            }
          });
        }
      },
      null,
      { enableHighAccuracy: true, timeout: 30000 }
    );
    geoWatchRef.current = watchId;

    return () => {
      cleared = true;
      navigator.geolocation.clearWatch(watchId);
      geoWatchRef.current = null;
    };
  }, [item?.status, item?.latitude, item?.longitude, jhaInfo, user?.role, loadJha]);

  const openCloseModal = () => {
    setCloseRack(racks[0]?.id ? String(racks[0].id) : '');
    setCloseMatches(null); setCloseNotes('');
    setShowCloseModal(true);
  };

  // Prompt to return unused material — fires AFTER the FMP is submitted (field handoff), not at close.
  const checkMaterialReturn = async () => {
    try {
      const co = await api(`/materials/checkout/work-item/${id}`);
      const outstanding = (co.items || []).filter(i => Number(i.qty_out) - Number(i.qty_returned) > 0);
      if (co.checkout && outstanding.length) {
        setReturnCheckout(co.checkout);
        setReturnItems(outstanding);
        const init = {}; outstanding.forEach(i => { init[i.id] = { checked: false, qty: Number(i.qty_out) - Number(i.qty_returned) }; });
        setReturnChecks(init);
        setShowReturnModal(true);
      }
    } catch (e) { /* no checkout — ignore */ }
  };

  const startClose = async () => { openCloseModal(); };

  const submitReturn = async () => {
    setReturnBusy(true);
    try {
      const chosen = returnItems
        .filter(i => returnChecks[i.id]?.checked && Number(returnChecks[i.id]?.qty) > 0)
        .map(i => ({ checkout_item_id: i.id, qty: Number(returnChecks[i.id].qty) }));
      let taskId = null;
      if (chosen.length) {
        const res = await api('/materials/return-tasks', { method: 'POST', body: JSON.stringify({ work_item_id: Number(id), checkout_id: returnCheckout.id, items: chosen }) });
        taskId = res?.task_id;
      }
      setShowReturnModal(false);
      // A return task was created — offer to start it now, or leave it in the My Work queue.
      if (taskId && window.confirm('A material return task has been created.\n\nStart the task now?  (No = it waits in your My Work queue)')) {
        await api(`/materials/return-tasks/${taskId}/start`, { method: 'PATCH' }).catch(() => {});
        if (window.confirm('Need directions to the stock yard?')) {
          try {
            const locs = await api('/materials/stock-locations');
            const sl = Array.isArray(locs) && locs[0];
            if (sl?.latitude && sl?.longitude) window.open(`https://maps.google.com/?q=${sl.latitude},${sl.longitude}`, '_blank');
          } catch (e) { /* ignore */ }
        }
        await api('/time-entries/start', { method: 'POST', body: JSON.stringify({ work_item_id: Number(id), activity_type: 'return_drive', notes: 'Return drive to stock' }) }).catch(() => {});
      }
    } finally { setReturnBusy(false); }
  };

  const doReassign = async () => {
    if (!reassignTech) return;
    const res = await api(`/work-items/${id}`, { method: 'PATCH', body: JSON.stringify({ assigned_tech: Number(reassignTech), status_note: 'Reassigned by manager' }) });
    if (res?.error) { alert(res.error); return; }
    setShowReassign(false); setReassignTech(''); load();
  };

  const updateStatus = async (status) => {
    if (status === 'closed' && item?.work_category === 'Project') {
      startClose();
      return;
    }
    setUpdatingStatus(true);
    const res = await api(`/work-items/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
    setUpdatingStatus(false);
    if (res?.error) { alert(res.error); return; }
    load();
    if (status === 'in_progress') {
      setJhaInfo(null); // reset so loadJha re-fetches
      setShowJHA(true);
    }
  };

  const confirmClose = async () => {
    if (closeRack && closeMatches === null) return;
    setClosingBusy(true);
    try {
      if (closeRack) {
        await api('/fmp-verifications', {
          method: 'POST',
          body: JSON.stringify({ rack_id: Number(closeRack), rack_matches: closeMatches, notes: closeNotes || null, verification_type: 'job_close', work_item_id: Number(id) }),
        });
      }
      await api(`/work-items/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'closed' }) });
      setShowCloseModal(false);
      load();
    } finally {
      setClosingBusy(false);
    }
  };

  const setCapital = async (val) => {
    setCapitalBusy(true);
    const res = await api(`/work-items/${id}`, { method: 'PATCH', body: JSON.stringify({ is_capital: val }) });
    setCapitalBusy(false);
    if (res?.error) { alert(res.error); return; }
    load();
  };

  const confirmArrivalFmp = async () => {
    setArrivalBusy(true);
    try {
      if (arrivalRack && arrivalMatches !== null) {
        await api('/fmp-verifications', {
          method: 'POST',
          body: JSON.stringify({ rack_id: Number(arrivalRack), rack_matches: arrivalMatches, notes: arrivalNotes || null, verification_type: 'arrival', work_item_id: Number(id) }),
        });
      }
    } finally {
      setArrivalBusy(false);
      setShowArrivalFmp(false);
    }
  };

  const generateMop = async () => {
    await api(`/work-items/${id}/generate-mop`, { method: 'POST' });
    load();
  };

  const toggleStep = async (step, completed) => {
    await api(`/work-items/${id}/mop/${step.id}`, { method: 'PATCH', body: JSON.stringify({ completed }) });
    load();
  };

  const editStep = async (step, description) => {
    await api(`/work-items/${id}/mop/${step.id}`, { method: 'PATCH', body: JSON.stringify({ description }) });
    load();
  };

  const deleteStep = async (step) => {
    await api(`/work-items/${id}/mop/${step.id}`, { method: 'DELETE' });
    load();
  };

  const addStep = async () => {
    if (!newStep.trim()) return;
    await api(`/work-items/${id}/mop`, { method: 'POST', body: JSON.stringify({ description: newStep }) });
    setNewStep('');
    load();
  };

  const submitChange = async () => {
    const res = await api(`/work-items/${id}/submit-change`, { method: 'POST' });
    if (res.error) { alert(res.error); return; }
    load();
  };

  const approveChange = async () => {
    const res = await api(`/work-items/${id}/approve-change`, { method: 'POST' });
    if (res.error) { alert(res.error); return; }
    load();
  };

  const approveMop = async () => {
    const res = await api(`/work-items/${id}/approve-mop`, { method: 'POST' });
    if (res.error) { alert(res.error); return; }
    load();
  };

  const submitSiteVisit = async (form) => {
    const res = await api(`/work-items/${id}/site-visit`, { method: 'POST', body: JSON.stringify(form) });
    if (res.error) { alert(res.error); return; }
    setShowSiteVisit(false);
    load();
  };

  const previewFMP = async (form) => {
    const payload = { work_item_id: parseInt(id), ...form };
    const preview = await api('/fmps/preview', { method: 'POST', body: JSON.stringify(payload) });
    setFmpPreview(preview);
    setFmpPreviewForm(payload);
  };

  const submitFMP = async () => {
    if (!fmpPreviewForm) return;
    setSubmittingFmp(true);
    await api('/fmps/generate', { method: 'POST', body: JSON.stringify(fmpPreviewForm) });
    setSubmittingFmp(false);
    setFmpPreview(null);
    setFmpPreviewForm(null);
    setShowFMPForm(false);
    await checkMaterialReturn();   // FMP done → prompt to return unused material, then Field Complete
    load();
  };

  const verifyFMP = async (fmpId) => {
    await api(`/fmps/${fmpId}/verify`, { method: 'PATCH' });
    load();
  };

  const publishFMP = async (fmpId) => {
    await api(`/fmps/${fmpId}/publish`, { method: 'PATCH' });
    load();
  };

  if (loading) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0f172a', color: '#64748b', fontFamily: 'sans-serif' }}>Loading...</div>;
  if (!item) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0f172a', color: '#64748b', fontFamily: 'sans-serif' }}>Work item not found.</div>;

  const mopDone = item.mop_steps?.filter(s => s.completed).length || 0;
  const mopTotal = item.mop_steps?.length || 0;
  const mopPct = mopTotal > 0 ? Math.round((mopDone / mopTotal) * 100) : 0;
  const isProject = item.work_category === 'Project';
  const itemWorkflow = workflowFor(item.work_category);
  const canEdit = ['field_engineer','rpm','admin'].includes(user?.role);
  const isFE = ['field_engineer','admin'].includes(user?.role);
  const isDesign = ['design','admin'].includes(user?.role);
  const isManager = ['rpm','field_manager','admin','director','managing_director','management'].includes(user?.role);
  const currentStatusIdx = itemWorkflow.indexOf(item.status);

  const PROJECT_ACTIONS = {
    submitted:        { label: 'Send to FE Review →',   next: 'fe_review',    roles: ['rpm','admin','field_engineer'] },
    fe_review:        { label: 'Begin Site Visit →',    next: 'fe_site_visit',roles: ['field_engineer','admin'] },
    fe_site_visit:    null,
    change_submitted: null,
    change_approved:  null,
    mop_draft:        null,
    mop_approved:     { label: 'Assign Tech & Schedule →', next: 'ready_to_work', roles: ['rpm','admin'] },
    ready_to_work:    { label: 'Mark In Progress →',    next: 'in_progress',  roles: ['tech','field_engineer','rpm','admin'] },
    in_progress:      null,
    fmp_pending:      null,
    design_review:    null,
    closed:           null,
  };

  const INCIDENT_ACTIONS = {
    new:          { label: 'Dispatch to Tech ->', next: 'dispatched', roles: ['rpm','field_manager','admin','director','managing_director','management'], needsSchedule: true },
    dispatched:   { label: 'Mark Accepted ->',    next: 'accepted',   roles: ['tech','field_engineer','rpm','field_manager','admin','director','managing_director','management'] },
    accepted:     { label: 'Mark In Progress ->', next: 'in_progress',roles: ['tech','field_engineer','rpm','field_manager','admin','director','managing_director','management'] },
    in_progress:  { label: 'Resolve ->',          next: 'resolved',   roles: ['tech','field_engineer','rpm','field_manager','admin','director','managing_director','management'] },
    resolved:     { label: 'Close ->',            next: 'closed',     roles: ['rpm','field_manager','admin','director','managing_director','management'] },
    closed:       null,
    cancelled:    null,
  };

  const NEXT_ACTIONS = isProject ? PROJECT_ACTIONS : INCIDENT_ACTIONS;
  const nextAction = NEXT_ACTIONS[item.status];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#0f172a', fontFamily: 'sans-serif', minHeight: 0, overflowY: 'auto' }}>

      {/* Header */}
      <div style={{ backgroundColor: '#1e293b', borderBottom: '1px solid #334155', padding: '14px 20px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
          <button onClick={() => navigate('/ops')} style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid #334155', color: '#94a3b8', padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13, flexShrink: 0 }}>← Back</button>
          <a href={`${API}/pdf-export/work-item/${item.id}?token=${localStorage.getItem('token')}`} target="_blank" rel="noreferrer"
            style={{ backgroundColor: '#1e3a5f', border: '1px solid #3b82f6', color: '#3b82f6', padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13, flexShrink: 0, textDecoration: 'none' }}>
            ⬇ PDF
          </a>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 18, marginBottom: 4 }}>{item.title}</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {item.wo_number && <span style={{ fontSize: 12, color: '#f59e0b', backgroundColor: '#1c1008', padding: '2px 8px', borderRadius: 4 }}>WO: {item.wo_number}</span>}
              {item.po_number && <span style={{ fontSize: 12, color: '#a78bfa', backgroundColor: '#1a1030', padding: '2px 8px', borderRadius: 4 }}>PO: {item.po_number}</span>}
              {item.sn_ticket && <span style={{ fontSize: 12, color: '#3b82f6', backgroundColor: '#1e3a5f', padding: '2px 8px', borderRadius: 4 }}>SN: {item.sn_ticket}</span>}
              <span style={{ fontSize: 11, fontWeight: 'bold', padding: '2px 8px', borderRadius: 4, backgroundColor: PRIORITY_COLORS[item.priority] + '22', color: PRIORITY_COLORS[item.priority] }}>{item.priority}</span>
              <span style={{ fontSize: 11, fontWeight: 'bold', padding: '2px 8px', borderRadius: 4, backgroundColor: STATUS_COLORS[item.status] + '22', color: STATUS_COLORS[item.status] }}>{STATUS_LABELS[item.status]}</span>
              {item.location_name && <span style={{ fontSize: 12, color: '#94a3b8' }}>📍 {item.location_name}</span>}
            </div>
          </div>
        </div>

        {/* Status pipeline */}
        <div style={{ display: 'flex', gap: 2, overflowX: 'auto', paddingBottom: 4 }}>
          {itemWorkflow.map((s, i) => (
            <div key={s} style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <div style={{ padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: i === currentStatusIdx ? 'bold' : 'normal', backgroundColor: i < currentStatusIdx ? '#166534' : i === currentStatusIdx ? STATUS_COLORS[s] : '#1e293b', color: i < currentStatusIdx ? '#4ade80' : i === currentStatusIdx ? 'white' : '#475569' }}>
                {STATUS_LABELS[s]}
              </div>
              {i < itemWorkflow.length - 1 && <div style={{ width: 16, height: 1, backgroundColor: i < currentStatusIdx ? '#166534' : '#334155' }} />}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left: details */}
        <div style={{ width: 280, flexShrink: 0, borderRight: '1px solid #1e293b', padding: 16, overflowY: 'auto' }}>
          {isProject && ['tech','field_engineer','admin'].includes(user?.role) && item.status !== 'closed'
            && (item.tech_acceptance_status === 'accepted' || item.tech_acceptance_status === 'not_required' || user?.role === 'admin') && (
            <InstallFlow item={item} onRefresh={load} onBeginInstall={() => updateStatus('in_progress')} onFieldComplete={() => updateStatus('field_install_complete')} onSubmitFMP={() => setShowFMPForm(true)} />
          )}
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Assignments</div>
            {[['Field Engineer', item.fe_name], ['Tech', item.tech_name], ['RPM', item.rpm_name], ['Created By', item.created_by_name]].map(([label, val]) => val ? (
              <div key={label} style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 11, color: '#475569' }}>{label}</div>
                <div style={{ fontSize: 13, color: '#e2e8f0' }}>{val}</div>
              </div>
            ) : null)}
          </div>

          {canEdit && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Site / Location</div>
              <select value={item.location_id || ''} onChange={e => setLocation(e.target.value)} style={{ width: '100%', padding: '7px 9px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13 }}>
                <option value=''>— No site set —</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Details</div>
            {[
              ['WO #', item.wo_number],
              ['PO #', item.po_number],
              ['SN Ticket', item.sn_ticket],
              ['Work Type', item.work_type?.replace(/_/g,' ')],
              ['Type', item.type?.replace(/_/g,' ')],
              ['Scheduled', item.scheduled_date ? new Date(item.scheduled_date).toLocaleDateString() : '—'],
              ['Created', new Date(item.created_at).toLocaleDateString()],
              ['Rack', item.rack_label],
            ].filter(([,v]) => v).map(([l, v]) => (
              <div key={l} style={{ marginBottom: 5, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, color: '#475569' }}>{l}</span>
                <span style={{ fontSize: 12, color: '#94a3b8', textTransform: 'capitalize' }}>{v}</span>
              </div>
            ))}
          </div>

          {item.description && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Description</div>
              <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.5 }}>{item.description}</div>
            </div>
          )}

          {/* Site arrival indicator */}
          {item.status === 'in_progress' && jhaInfo?.site_arrival_at && (
            <div style={{ marginBottom: 12, padding: '8px 10px', backgroundColor: '#052e16', border: '1px solid #16a34a44', borderRadius: 7 }}>
              <div style={{ color: '#4ade80', fontSize: 12, fontWeight: 'bold' }}>📍 On Site</div>
              <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>Arrived {new Date(jhaInfo.site_arrival_at).toLocaleTimeString()}</div>
            </div>
          )}

          {/* JHA not yet submitted warning */}
          {item.status === 'in_progress' && jhaInfo && jhaInfo.status === 'draft' && (
            <div style={{ marginBottom: 12, padding: '8px 10px', backgroundColor: '#431407', border: '1px solid #ea580c44', borderRadius: 7 }}>
              <div style={{ color: '#fb923c', fontSize: 12, fontWeight: 'bold' }}>⚠ JHA Not Submitted</div>
              <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>Complete the JHA before starting work</div>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {isManager && (
              <button onClick={() => { setReassignTech(item.assigned_tech ? String(item.assigned_tech) : ''); setShowReassign(true); }}
                style={{ padding: '9px', backgroundColor: '#0f766e', color: '#ccfbf1', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                🔄 Reassign Tech
              </button>
            )}
            {nextAction && nextAction.roles.includes(user?.role) && (
              <button
                onClick={() => nextAction.needsSchedule || nextAction.next === 'ready_to_work' ? setShowSchedule(true) : updateStatus(nextAction.next)}
                disabled={updatingStatus}
                style={{ padding: '9px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                {nextAction.label}
              </button>
            )}
            {/* Site visit — FE only, while in fe_site_visit status */}
            {isProject && isFE && item.status === 'fe_site_visit' && (
              <button onClick={() => setShowSiteVisit(o => !o)} style={{ padding: '9px', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                {showSiteVisit ? '✕ Close' : '📋 Site Visit'}
              </button>
            )}

            {/* Submit change — FE after site visit */}
            {isProject && isFE && item.status === 'fe_site_visit' && item.site_visit && (
              <button onClick={submitChange} style={{ padding: '9px', backgroundColor: '#7c2d12', color: '#fb923c', border: '1px solid #c2410c44', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                → Submit Change for Approval
              </button>
            )}

            {/* Approve change — admin only (SN webhook replaces this later) */}
            {isProject && user?.role === 'admin' && item.status === 'change_submitted' && (
              <button onClick={approveChange} style={{ padding: '9px', backgroundColor: '#166534', color: '#4ade80', border: '1px solid #16632a', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                ✓ Approve Change
              </button>
            )}

            {/* Generate MOP — FE/admin only, after change approved or while in draft */}
            {isProject && isFE && ['change_approved','mop_draft'].includes(item.status) && (
              <button onClick={generateMop} style={{ padding: '9px', backgroundColor: '#0f172a', color: '#f59e0b', border: '1px solid #f59e0b44', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
                ⚡ {mopTotal > 0 ? 'Regenerate MOP' : 'Generate MOP'}
              </button>
            )}

            {/* Approve MOP — FE/admin sign-off */}
            {isProject && isFE && item.status === 'mop_draft' && mopTotal > 0 && (
              <button onClick={approveMop} style={{ padding: '9px', backgroundColor: '#166534', color: '#4ade80', border: '1px solid #16632a', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                ✓ Approve MOP
              </button>
            )}

            {/* JHA — open on in_progress for tech/FE */}
            {item.status === 'in_progress' && ['tech', 'field_engineer', 'admin'].includes(user?.role) && (
              <button onClick={() => setShowJHA(true)} style={{ padding: '9px', backgroundColor: '#0e7490', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                🦺 Open JHA
              </button>
            )}

            {/* Generate FMP — in progress */}
            {isProject && ['in_progress','field_install_complete'].includes(item.status) && (
              <button onClick={() => setShowFMPForm(o => !o)} style={{ padding: '9px', backgroundColor: '#c2410c', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                {showFMPForm ? '✕ Close' : '📄 Generate FMP'}
              </button>
            )}
          </div>

          {/* Site visit form */}
          {showSiteVisit && <div style={{ marginTop: 12 }}><SiteVisitForm workItemId={id} siteLat={item.latitude} siteLon={item.longitude} onSubmit={submitSiteVisit} /></div>}

          {/* FMP form */}
          {showFMPForm && <div style={{ marginTop: 12 }}><FMPForm workItemId={id} racks={racks} locationId={item.location_id} navigate={navigate} onSubmit={previewFMP} /></div>}
        </div>

        {/* Right: tabs */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Tab bar */}
          <div style={{ display: 'flex', borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
            {(isProject
              ? [['mop', `MOP (${mopDone}/${mopTotal})`], ['site_visit', 'Site Visit'], ['fmps', `FMPs (${item.fmps?.length || 0})`], ['material', 'Material'], ['history', 'History']]
              : [['history', 'History'], ['material', 'Material']]
            ).map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)} style={{ padding: '10px 16px', border: 'none', borderBottom: tab === key ? '2px solid #3b82f6' : '2px solid transparent', backgroundColor: 'transparent', color: tab === key ? '#93c5fd' : '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: tab === key ? 'bold' : 'normal' }}>
                {label}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {/* MOP Tab */}
            {tab === 'mop' && (
              <div>
                {/* FE — document upload + read-only checklist view */}
                {isFE ? (
                  <>
                    <MopDocumentUpload workItemId={parseInt(id)} />
                    <MopDocumentViewer workItemId={parseInt(id)} />
                    {mopTotal > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 5 }}>
                          <span>MOP Progress</span><span>{mopPct}% — {mopDone} of {mopTotal} steps</span>
                        </div>
                        <div style={{ height: 6, backgroundColor: '#1e293b', borderRadius: 3 }}>
                          <div style={{ height: 6, borderRadius: 3, width: `${mopPct}%`, backgroundColor: mopPct === 100 ? '#22c55e' : '#3b82f6', transition: 'width 0.3s' }} />
                        </div>
                        <div style={{ marginTop: 12 }}>
                          {[...new Set(item.mop_steps.map(s => s.category))].map(cat => (
                            <div key={cat} style={{ marginBottom: 14 }}>
                              <div style={{ color: '#475569', fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 7 }}>{cat}</div>
                              {item.mop_steps.filter(s => s.category === cat).map(step => (
                                <div key={step.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', backgroundColor: '#1e293b', borderRadius: 6, marginBottom: 6, border: `1px solid ${step.completed ? '#16a34a44' : '#334155'}` }}>
                                  <span style={{ fontSize: 14, marginTop: 1 }}>{step.completed ? '✅' : '⬜'}</span>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ color: step.completed ? '#86efac' : '#cbd5e1', fontSize: 13, textDecoration: step.completed ? 'line-through' : 'none' }}>{step.description}</div>
                                    {step.completed && step.completed_by_name && (
                                      <div style={{ color: '#475569', fontSize: 11, marginTop: 3 }}>
                                        Done by {step.completed_by_name} · {new Date(step.completed_at).toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* Tech / RPM — documents to download + checklist to execute */
                  <>
                    <MopDocumentViewer workItemId={parseInt(id)} />

                    {mopTotal > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 5 }}>
                          <span>Completion</span><span>{mopPct}% — {mopDone} of {mopTotal} steps</span>
                        </div>
                        <div style={{ height: 6, backgroundColor: '#1e293b', borderRadius: 3 }}>
                          <div style={{ height: 6, borderRadius: 3, width: `${mopPct}%`, backgroundColor: mopPct === 100 ? '#22c55e' : '#3b82f6', transition: 'width 0.3s' }} />
                        </div>
                      </div>
                    )}

                    {mopTotal === 0 ? (
                      <div style={{ textAlign: 'center', padding: 32, color: '#475569' }}>
                        <div style={{ fontSize: 24, marginBottom: 8 }}>📋</div>
                        <div style={{ fontSize: 14 }}>No MOP steps generated yet</div>
                      </div>
                    ) : (
                      <>
                        {[...new Set(item.mop_steps.map(s => s.category))].map(cat => (
                          <div key={cat} style={{ marginBottom: 16 }}>
                            <div style={{ color: '#475569', fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 7 }}>{cat}</div>
                            {item.mop_steps.filter(s => s.category === cat).map(step => (
                              <CheckItem key={step.id} step={step} onToggle={toggleStep} onDelete={deleteStep} onEdit={editStep} canEdit={['tech', 'admin'].includes(user?.role)} />
                            ))}
                          </div>
                        ))}
                        {user?.role === 'tech' && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <input value={newStep} onChange={e => setNewStep(e.target.value)} onKeyDown={e => e.key === 'Enter' && addStep()} placeholder="Add custom step..."
                              style={{ flex: 1, padding: '8px 10px', backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13 }} />
                            <button onClick={addStep} style={{ padding: '8px 14px', backgroundColor: '#334155', color: '#94a3b8', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>+ Add</button>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Site Visit Tab */}
            {tab === 'site_visit' && (
              <div>
                {item.site_visit ? (
                  <div style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: 16, border: '1px solid #334155' }}>
                    <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 14, marginBottom: 12 }}>Site Visit — {new Date(item.site_visit.visited_at).toLocaleString()}</div>
                    <div style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>By: {item.site_visit.visited_by_name}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                      {[['Power Available', item.site_visit.power_available], ['Rack Space OK', item.site_visit.rack_space_ok], ['Fiber Accessible', item.site_visit.fiber_accessible], ['Site Safe', item.site_visit.site_safe]].map(([l, v]) => (
                        <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', backgroundColor: '#0f172a', borderRadius: 6 }}>
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>{l}</span>
                          <span style={{ fontSize: 12, fontWeight: 'bold', color: v === true ? '#22c55e' : v === false ? '#f87171' : '#64748b' }}>{v === true ? '✓ Yes' : v === false ? '✗ No' : '— N/A'}</span>
                        </div>
                      ))}
                    </div>
                    {item.site_visit.site_conditions && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>Site Conditions</div><div style={{ fontSize: 13, color: '#94a3b8' }}>{item.site_visit.site_conditions}</div></div>}
                    {item.site_visit.markups && <div style={{ marginBottom: 4 }}><div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>Scope Adjustments</div><div style={{ fontSize: 13, color: '#94a3b8' }}>{item.site_visit.markups}</div></div>}
                    <PhotoUpload
                      contextType="work_item"
                      contextId={parseInt(id)}
                      photoType="site_visit"
                      label="Site Visit Photos"
                      canUpload={isFE}
                    />
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: 32, color: '#475569' }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>🔍</div>
                    <div style={{ fontSize: 14 }}>No site visit recorded yet</div>
                  </div>
                )}
              </div>
            )}

            {/* FMPs Tab */}
            {tab === 'fmps' && (
              <div>
                {item.fmps?.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 32, color: '#475569' }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>📄</div>
                    <div style={{ fontSize: 14 }}>No FMPs generated yet</div>
                  </div>
                ) : item.fmps?.map(fmp => (
                  <div key={fmp.id} style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: 14, marginBottom: 10, border: '1px solid #334155' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div>
                        <span style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 13 }}>{fmp.fmp_type?.replace(/_/g,' ').toUpperCase()}</span>
                        <span style={{ fontSize: 11, marginLeft: 8, padding: '2px 6px', borderRadius: 4, backgroundColor: fmp.status === 'published' ? '#166534' : fmp.status === 'no_change' ? '#374151' : '#7c2d12', color: fmp.status === 'published' ? '#4ade80' : fmp.status === 'no_change' ? '#94a3b8' : '#fb923c' }}>
                          {fmp.status?.replace(/_/g,' ')}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{new Date(fmp.generated_at).toLocaleDateString()}</div>
                    </div>
                    {fmp.no_change && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>No changes made — marked as reviewed</div>}
                    {fmp.change_summary && <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>{fmp.change_summary}</div>}
                    <div style={{ fontSize: 11, color: '#475569', marginBottom: 10 }}>
                      Generated by: {fmp.generated_by_name}
                      {fmp.verified_by_name && ` · Verified by: ${fmp.verified_by_name}`}
                    </div>
                    {isFE && fmp.status === 'draft' && (
                      <button onClick={() => verifyFMP(fmp.id)} style={{ padding: '7px 14px', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}>
                        ✓ Verify & Send to Design
                      </button>
                    )}
                    {isDesign && fmp.status === 'pending_design' && (
                      <button onClick={() => publishFMP(fmp.id)} style={{ padding: '7px 14px', backgroundColor: '#166534', color: '#4ade80', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}>
                        ✓ Publish FMP
                      </button>
                    )}
                    <PhotoUpload
                      contextType="fmp"
                      contextId={fmp.id}
                      photoType="rack_install"
                      label="Rack / Installation Photos"
                      canUpload={['tech','field_engineer','admin'].includes(user?.role)}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Material Tab */}
            {tab === 'material' && (
              <MaterialPanel workItemId={parseInt(id)} role={user?.role} />
            )}

            {/* History Tab */}
            {tab === 'history' && (
              <div>
                {item.history?.map((h, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #1e293b' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: STATUS_COLORS[h.to_status] || '#475569', marginTop: 5, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: '#e2e8f0' }}>
                        {h.from_status ? <><span style={{ color: STATUS_COLORS[h.from_status] }}>{STATUS_LABELS[h.from_status]}</span> → </> : ''}
                        <span style={{ color: STATUS_COLORS[h.to_status], fontWeight: 'bold' }}>{STATUS_LABELS[h.to_status]}</span>
                      </div>
                      {h.notes && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{h.notes}</div>}
                      <div style={{ fontSize: 11, color: '#334155', marginTop: 3 }}>
                        {h.changed_by_name || 'System'} · {new Date(h.changed_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <FMPPreviewModal
        fmp={fmpPreview}
        onClose={() => { setFmpPreview(null); setFmpPreviewForm(null); }}
        onSubmit={submitFMP}
        submitting={submittingFmp}
      />

      {/* JHA modal */}
      {showJHA && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 5000, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px', flexShrink: 0 }}>
            <button onClick={() => { setShowJHA(false); loadJha(); }} style={{ backgroundColor: '#1e293b', border: '1px solid #334155', color: '#94a3b8', padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>✕ Close JHA</button>
          </div>
          <div style={{ flex: 1, maxWidth: 860, width: '100%', margin: '0 auto', padding: '0 16px 32px' }}>
            <JHAForm workItemId={parseInt(id)} onClose={() => { setShowJHA(false); loadJha(); }} />
          </div>
        </div>
      )}

      {/* FMP always-on-close modal */}
      {/* Arrival FMP Spot-Check Modal */}
      {showArrivalFmp && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 4500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 480, backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 24 }}>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 17, marginBottom: 4 }}>📍 You've arrived on site</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 18 }}>
              Quick FMP spot-check — does the rack match the current FMP?
            </div>
            {racks.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Rack</div>
                <select value={arrivalRack} onChange={e => setArrivalRack(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13, boxSizing: 'border-box' }}>
                  {racks.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
            )}
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Rack matches FMP?</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {[{ val: true, label: '✓ Yes — matches', color: '#22c55e' }, { val: false, label: '✗ No — discrepancy', color: '#ef4444' }].map(opt => (
                  <button key={String(opt.val)} onClick={() => setArrivalMatches(opt.val)} style={{
                    flex: 1, padding: '10px', borderRadius: 7,
                    border: `2px solid ${arrivalMatches === opt.val ? opt.color : '#334155'}`,
                    backgroundColor: arrivalMatches === opt.val ? `${opt.color}22` : '#0f172a',
                    color: arrivalMatches === opt.val ? opt.color : '#64748b',
                    cursor: 'pointer', fontWeight: 700, fontSize: 13,
                  }}>{opt.label}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Notes (optional)</div>
              <textarea value={arrivalNotes} onChange={e => setArrivalNotes(e.target.value)} rows={2}
                style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowArrivalFmp(false)} style={{ flex: 1, padding: 10, backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Skip</button>
              <button onClick={confirmArrivalFmp} disabled={arrivalBusy || arrivalMatches === null}
                style={{ flex: 2, padding: 10, backgroundColor: '#0369a1', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800, opacity: arrivalMatches === null ? 0.5 : 1 }}>
                {arrivalBusy ? 'Saving…' : 'Log Arrival Check'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReturnModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 4100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 500, backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 24 }}>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Any material to return to stock?</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>Check what is going back. A return-to-stock task is created for what you select.</div>
            <div style={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12, marginBottom: 18 }}>
              {returnItems.map(i => {
                const c = returnChecks[i.id] || { checked: false, qty: 0 };
                const max = Number(i.qty_out) - Number(i.qty_returned);
                return (
                  <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <label style={{ flex: 1, fontSize: 13, color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={c.checked} onChange={e => setReturnChecks(m => ({ ...m, [i.id]: { ...c, checked: e.target.checked } }))} />
                      {i.description} <span style={{ color: '#64748b' }}>(out {max} {i.unit || ''})</span>
                    </label>
                    {c.checked && (
                      <input type="number" min="0" max={max} value={c.qty}
                        onChange={e => setReturnChecks(m => ({ ...m, [i.id]: { ...c, qty: Math.min(Number(e.target.value) || 0, max) } }))}
                        style={{ width: 70, padding: '6px 8px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13 }} />
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowReturnModal(false)} style={{ flex: 1, padding: 10, backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Nothing to return</button>
              <button onClick={submitReturn} disabled={returnBusy} style={{ flex: 2, padding: 10, backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>{returnBusy ? 'Saving…' : 'Create Return Task'}</button>
            </div>
          </div>
        </div>
      )}

      {showCloseModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 500, backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 24 }}>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Close Work Order</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 20 }}>
              Before closing, please log the FMP verification for this site visit.
            </div>

            {racks.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Rack</div>
                <select value={closeRack} onChange={e => setCloseRack(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13, boxSizing: 'border-box' }}>
                  <option value="">— Skip rack verification —</option>
                  {racks.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
            )}

            {closeRack && (
              <>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Rack matches FMP?</div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {[{ val: true, label: 'Yes — matches', color: '#22c55e' }, { val: false, label: 'No — discrepancy', color: '#ef4444' }].map(opt => (
                      <button key={String(opt.val)} onClick={() => setCloseMatches(opt.val)} style={{
                        flex: 1, padding: '10px', borderRadius: 7,
                        border: `2px solid ${closeMatches === opt.val ? opt.color : '#334155'}`,
                        backgroundColor: closeMatches === opt.val ? `${opt.color}22` : '#0f172a',
                        color: closeMatches === opt.val ? opt.color : '#64748b',
                        cursor: 'pointer', fontWeight: 700, fontSize: 13,
                      }}>{opt.label}</button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Notes (optional)</div>
                  <textarea value={closeNotes} onChange={e => setCloseNotes(e.target.value)} rows={2}
                    style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowCloseModal(false)} style={{ flex: 1, padding: 10, backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              <button onClick={confirmClose} disabled={closingBusy || (closeRack && closeMatches === null)}
                style={{ flex: 2, padding: 10, backgroundColor: '#6366f1', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800, opacity: (closeRack && closeMatches === null) ? 0.5 : 1 }}>
                {closingBusy ? 'Closing…' : 'Close Work Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Capital project classification — pops on a new imported Project that isn't classified yet */}
      {item.work_category === 'Project' && (item.is_capital === null || item.is_capital === undefined) && !capitalDismissed
        && ['admin','rpm','field_manager','director','managing_director','field_engineer','management'].includes(user?.role) && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 4200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 480, backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 24 }}>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 18, marginBottom: 4 }}>New Project — Classify</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>Confirm the imported details, then mark whether this is a capital project.</div>

            <div style={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 14, marginBottom: 18 }}>
              {[['Project', item.title], ['Work Order #', item.wo_number], ['PO #', item.po_number], ['SN Ticket', item.sn_ticket], ['Location', item.location_name]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                  <span style={{ color: '#64748b' }}>{k}</span><span style={{ color: '#e2e8f0', fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>

            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Is this a capital project?</div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <button onClick={() => setCapital(true)} disabled={capitalBusy} style={{ flex: 1, padding: '12px', borderRadius: 7, border: '2px solid #22c55e', backgroundColor: '#22c55e22', color: '#22c55e', cursor: 'pointer', fontWeight: 800, fontSize: 14 }}>Yes — Capital</button>
              <button onClick={() => setCapital(false)} disabled={capitalBusy} style={{ flex: 1, padding: '12px', borderRadius: 7, border: '2px solid #3b82f6', backgroundColor: '#3b82f622', color: '#60a5fa', cursor: 'pointer', fontWeight: 800, fontSize: 14 }}>No — O&amp;M</button>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button onClick={() => setCapitalDismissed(true)} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>Decide later</button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Tech Modal */}
      {showReassign && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 4300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 440, backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 24 }}>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Reassign Ticket</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>Pick who should work this. They get it as pending in their My Work; the current tech is removed.</div>
            <select value={reassignTech} onChange={e => setReassignTech(e.target.value)} style={{ width: '100%', padding: '9px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: '#e2e8f0', fontSize: 14, boxSizing: 'border-box', marginBottom: 18 }}>
              <option value="">— Select a tech —</option>
              {users.filter(u => ['tech', 'field_engineer'].includes(u.role)).map(u => (
                <option key={u.id} value={u.id}>{u.name} ({u.role === 'tech' ? 'Tech' : 'Field Engineer'})</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowReassign(false)} style={{ flex: 1, padding: 10, backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              <button onClick={doReassign} disabled={!reassignTech} style={{ flex: 2, padding: 10, backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800, opacity: reassignTech ? 1 : 0.5 }}>Reassign</button>
            </div>
          </div>
        </div>
      )}

      {showSchedule && (
        <ScheduleModal
          users={users}
          currentTech={item.assigned_tech}
          currentDate={item.scheduled_date}
          onCancel={() => setShowSchedule(false)}
          onConfirm={async ({ assigned_tech, scheduled_date, notes }) => {
            setUpdatingStatus(true);
            const res = await api(`/work-items/${id}`, {
              method: 'PATCH',
              body: JSON.stringify({ status: isProject ? 'ready_to_work' : 'dispatched', assigned_tech: assigned_tech || null, scheduled_date: scheduled_date || null, status_note: notes || null }),
            });
            setUpdatingStatus(false);
            if (res?.error) { alert(res.error); return; }
            setShowSchedule(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function ScheduleModal({ users, currentTech, currentDate, onCancel, onConfirm }) {
  const techs = users.filter(u => ['tech','field_engineer'].includes(u.role));
  const [form, setForm] = useState({
    assigned_tech: currentTech || '',
    scheduled_date: currentDate ? currentDate.slice(0, 10) : '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.assigned_tech) { alert('Please select a technician'); return; }
    if (!form.scheduled_date) { alert('Please select a scheduled date'); return; }
    setSubmitting(true);
    await onConfirm(form);
    setSubmitting(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ backgroundColor: '#1e293b', borderRadius: 12, padding: 24, width: '100%', maxWidth: 400, border: '1px solid #334155', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 16, marginBottom: 4 }}>Assign Tech & Schedule</div>
        <div style={{ color: '#64748b', fontSize: 12, marginBottom: 20 }}>Select a technician and set the scheduled date before releasing to work.</div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Technician *</label>
          <select value={form.assigned_tech} onChange={e => setForm(p => ({ ...p, assigned_tech: e.target.value }))}
            style={{ width: '100%', padding: '9px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13 }}>
            <option value=''>— Select technician —</option>
            {techs.map(u => (
              <option key={u.id} value={u.id}>{u.name} ({u.role === 'tech' ? 'Tech' : 'Field Engineer'})</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Scheduled Date *</label>
          <input type="date" value={form.scheduled_date} onChange={e => setForm(p => ({ ...p, scheduled_date: e.target.value }))}
            style={{ width: '100%', padding: '9px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13, boxSizing: 'border-box' }} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Notes (optional)</label>
          <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2}
            placeholder="Any instructions for the tech..."
            style={{ width: '100%', padding: '9px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: 10, backgroundColor: '#0f172a', color: '#94a3b8', border: '1px solid #334155', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button onClick={submit} disabled={submitting} style={{ flex: 2, padding: 10, backgroundColor: submitting ? '#374151' : '#2563eb', color: 'white', border: 'none', borderRadius: 7, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 'bold' }}>
            {submitting ? 'Scheduling…' : 'Confirm & Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}
